'use client';

/**
 * AI Assistant Hub — global mount of the floating Asistente Hub chat button.
 *
 * V128-AI-HUB-ASSISTANT-GLOBAL (Founder directive: "1 chat con el asistente
 * de IA capacitado para ayudar en cada área del sistema, en cualquier
 * idioma").
 *
 * This wrapper reads the current Next.js pathname via `usePathname()` and
 * renders `<AIChatWidget assistantId="asistente-hub" pageContext={...}>`
 * with the matching area ID. Mounted ONCE inside `AppShell` (after
 * `<AdCardSidebar/>`) so it appears on EVERY `/app/*`, `/seller/*`, and
 * authed `/market/*` route automatically — no per-page wiring needed.
 *
 * EXCLUDED fullscreen routes (have their own in-call chat — mounting the
 * floating button would obscure the call UI):
 *   - `/app/conferences/[roomId]` — videoconf in-call chat
 *   - `/app/entrevista/[roomId]` — interview in-call chat
 *
 * Anti-P-382 (REUSE, don't reinvent): no new widget logic — wraps the
 * existing `<AIChatWidget>` (412L, fully featured: localStorage history,
 * suggestions, retry, auth-aware routing, 60s timeout). The only new
 * behavior is the pathname→area mapping in `ai-hub-area-map.ts`.
 *
 * Anti-P-041: 0 `as any` — `pathnameToAreaId` returns the strict `AreaId`
 * union. Anti-P-042: 0 `console.log`.
 *
 * Anti-P-383 (file size ≤500L): this file is ~80L — well under the cap.
 */

import { usePathname } from 'next/navigation';
import { AIChatWidget } from './ai-chat-widget';
import { pathnameToAreaId, getAreaTitle } from './ai-hub-area-map';

// Regex list (NOT a string glob — Next.js pathnames are dynamic).
// Tests against the raw pathname (case-sensitive — route segments are
// case-sensitive by convention in the codebase).
const EXCLUDED_PATHS: ReadonlyArray<RegExp> = [
  // /app/conferences/[roomId] — in-call fullscreen chat (own chat panel).
  /^\/app\/conferences\/[^/]+$/,
  // /app/entrevista/[roomId] — interview fullscreen (own chat panel).
  /^\/app\/entrevista\/[^/]+$/,
];

export function AIAssistantHub() {
  const pathname = usePathname();

  // Skip rendering on fullscreen routes — they have their own in-call chat.
  if (EXCLUDED_PATHS.some((re) => re.test(pathname))) {
    return null;
  }

  const areaId = pathnameToAreaId(pathname);
  const { title, subtitle } = getAreaTitle(areaId);

  return (
    <AIChatWidget
      assistantId="asistente-hub"
      pageContext={{ path: pathname, section: areaId }}
      title={title}
      subtitle={subtitle}
      position="bottom-right"
    />
  );
}
